package com.iregained.site.components.sections.clinics.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinics.styles.ClinicsListItemStyle
import com.iregained.site.components.sections.clinics.styles.ClinicsSearchInputStyle
import com.iregained.site.components.sections.clinics.styles.ClinicsTwoPaneContainerStyle
import com.iregained.site.region.RegionConfig
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Input

// UNIQUE ANIMATIONS: Pincer Converge
@Composable
private fun Modifier.pincerLeft(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-50).px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
private fun Modifier.pincerRight(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun ClinicsListSection(data: ClinicsLocatorData = defaultClinicsData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    var searchQuery by remember { mutableStateOf("") }
    var activeClinics by remember { mutableStateOf<ClinicsData?>(null) }
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.listSectionId?.takeIf { it.isNotBlank() } ?: "clinics-list-section"

    val filteredClinics = remember(searchQuery, data.activeClinics) {
        if (searchQuery.isBlank()) data.activeClinics
        else data.activeClinics.filter {
            it.companyName.contains(searchQuery, ignoreCase = true) || it.address.contains(
                searchQuery, ignoreCase = true
            )
        }
    }

    Column(
        modifier = Modifier.id(targetId).styleModifier { property("scroll-margin-top", "100px") }.fillMaxWidth()
            .background(palette.custom.lightGray), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
            Box(modifier = ClinicsTwoPaneContainerStyle.toModifier()) {
                if (isDesktop) {
                    Row(modifier = Modifier.fillMaxWidth().height(750.px)) {
                        LocatorListPane(
                            data = data,
                            isDesktop = true,
                            searchQuery = searchQuery,
                            onSearchChange = { searchQuery = it },
                            clinics = filteredClinics,
                            onClinicClick = { activeClinics = it },
                            modifier = Modifier.width(50.percent).fillMaxHeight().pincerLeft(isVisible)
                        )
                        LocatorMapPane(
                            clinics = filteredClinics,
                            activeClinic = activeClinics,
                            modifier = Modifier.width(50.percent).fillMaxHeight().pincerRight(isVisible)
                        )
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        LocatorListPane(
                            data = data,
                            isDesktop = false,
                            searchQuery = searchQuery,
                            onSearchChange = { searchQuery = it },
                            clinics = filteredClinics,
                            onClinicClick = { activeClinics = it },
                            modifier = Modifier.fillMaxWidth().height(500.px).pincerLeft(isVisible)
                        )
                        LocatorMapPane(
                            clinics = filteredClinics,
                            activeClinic = activeClinics,
                            modifier = Modifier.fillMaxWidth().height(400.px)
                                .pincerRight(isVisible) // Slides in from right on mobile too
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun LocatorListPane(
    data: ClinicsLocatorData,
    isDesktop: Boolean,
    searchQuery: String,
    onSearchChange: (String) -> Unit,
    clinics: List<ClinicsData>,
    onClinicClick: (ClinicsData) -> Unit,
    modifier: Modifier
) {
    val palette = ColorMode.current.toSitePalette()
    val dynamicBorderModifier = if (isDesktop) {
        Modifier.borderRight(1.px, LineStyle.Solid, palette.custom.divider)
    } else {
        Modifier.borderBottom(4.px, LineStyle.Solid, palette.custom.divider)
    }

    Column(
        modifier = modifier.backgroundColor(palette.custom.lightGray).then(dynamicBorderModifier)
    ) {
        // Search Input Header
        Column(modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White).padding(20.px).zIndex(5)) {
            Input(
                type = InputType.Text, attrs = ClinicsSearchInputStyle.toModifier().toAttrs {
                    attr("placeholder", data.searchPlaceholder)
                    value(searchQuery)
                    onInput { e -> onSearchChange(e.value) }
                })
        }

        // Results Count Header
        Box(
            modifier = Modifier.fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = 12.px, leftRight = 20.px)
                .borderBottom(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.2f)).zIndex(2)
        ) {
            SpanText(
                "${clinics.size} ${data.locationsFoundText}",
                modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).fontSize(14.px)
            )
        }

        // Scrollable List (Now with floating cards)
        Column(
            modifier = Modifier.fillMaxWidth().weight(1f).padding(16.px).styleModifier { overflowY(Overflow.Auto) }) {

            if (clinics.isEmpty()) {
                SpanText(
                    text = if (searchQuery.isNotBlank()) data.noResultsMessage else data.emptyRegionMessage,
                    modifier = Modifier.padding(24.px).fontSize(16.px).color(palette.custom.darkText)
                        .textAlign(TextAlign.Center)
                )
            } else {
                clinics.forEach { clinic ->
                    key(clinic.companyName) {
                        Column(
                            modifier = ClinicsListItemStyle.toModifier().onClick { onClinicClick(clinic) }) {
                            // TRUST BADGE
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.margin(bottom = 8.px).gap(6.px)
                            ) {
                                Box(
                                    modifier = Modifier.size(8.px).borderRadius(50.percent)
                                        .backgroundColor(Colors.LightGreen)
                                )
                                Div(
                                    attrs = Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Colors.DarkGreen)
                                        .textTransform(TextTransform.Uppercase).toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = clinic.companyTag
                                                onDispose {}
                                            }
                                        })
                            }

                            Div(
                                attrs = Modifier.fontSize(20.px).color(palette.brand.primary)
                                    .fontWeight(FontWeight.Bold).margin(bottom = 4.px).toAttrs {
                                        ref { htmlElement ->
                                            htmlElement.innerHTML = clinic.companyName
                                            onDispose {}
                                        }
                                    })

                            SpanText(
                                clinic.address,
                                modifier = Modifier.fontSize(14.px).margin(bottom = 16.px)
                                    .color(palette.custom.darkText)
                            )

                            // Contact Info
                            Column(modifier = Modifier.gap(8.px).margin(bottom = 16.px)) {
                                if (clinic.phone.isNotBlank()) {
                                    A(
                                        href = "tel:${clinic.phone.replace(Regex("[^0-9+]"), "")}",
                                        attrs = Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center)
                                            .gap(8.px).textDecorationLine(TextDecorationLine.None)
                                            .color(palette.custom.darkText).toAttrs()
                                    ) {
                                        Image(Res.Icons.CALL, Modifier.size(16.px).flexShrink(0))
                                        SpanText(
                                            clinic.phone,
                                            modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Medium)
                                        )
                                    }
                                }
                                if (clinic.email.isNotBlank()) {
                                    A(
                                        href = "mailto:${clinic.email}",
                                        attrs = Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center)
                                            .gap(8.px).textDecorationLine(TextDecorationLine.None)
                                            .color(palette.custom.darkText).toAttrs()
                                    ) {
                                        Image(Res.Icons.SEND_EMAIL, Modifier.size(16.px).flexShrink(0))
                                        SpanText(
                                            clinic.email,
                                            modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Medium)
                                        )
                                    }
                                }
                            }

                            // Action Buttons
                            Row(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                                val encodedAddress = clinic.address.replace(" ", "+")
                                A(
                                    href = "https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}",
                                    attrs = Modifier.textDecorationLine(TextDecorationLine.None)
                                        .toAttrs { attr("target", "_blank") }) {
                                    Box(
                                        modifier = Modifier.backgroundColor(palette.brand.primary).color(Colors.White)
                                            .borderRadius(6.px).padding(topBottom = 8.px, leftRight = 16.px)
                                    ) {
                                        SpanText(
                                            data.directionsButtonText,
                                            modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                                        )
                                    }
                                }

                                if (clinic.websiteUrl.isNotBlank()) {
                                    A(
                                        href = clinic.websiteUrl,
                                        attrs = Modifier.textDecorationLine(TextDecorationLine.None)
                                            .toAttrs { attr("target", "_blank") }) {
                                        Box(
                                            modifier = Modifier.backgroundColor(palette.brand.primarySoft)
                                                .color(palette.brand.primary).borderRadius(6.px)
                                                .padding(topBottom = 8.px, leftRight = 16.px)
                                        ) {
                                            SpanText(
                                                data.websiteButtonText,
                                                modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
fun LocatorMapPane(
    clinics: List<ClinicsData>, activeClinic: ClinicsData?, modifier: Modifier
) {
    val googleMapsApiKey = "AIzaSyCkCXKJB7hJtqb1bJGSjpiAGomBHkGCd-E"

    Box(modifier = modifier.backgroundColor(Colors.White)) {
        if (googleMapsApiKey.isBlank()) {
            val query = activeClinic?.address ?: clinics.firstOrNull()?.address ?: RegionConfig.currentRegion.name
            val encodedQuery = query.replace(" ", "+")
            val mapEmbedUrl = "https://maps.google.com/maps?q=${encodedQuery}&t=&z=13&ie=UTF8&iwloc=&output=embed"

            Iframe(
                attrs = Modifier.fillMaxSize().border(0.px).toAttrs {
                    attr("src", mapEmbedUrl)
                    attr("loading", "lazy")
                    attr("referrerpolicy", "no-referrer-when-downgrade")
                })
        } else {
            val locationsJson = clinics.joinToString(",\n") {
                "{ name: \"${it.companyName.replace("\"", "\\\"")}\", address: \"${
                    it.address.replace(
                        "\"", "\\\""
                    )
                }\" }"
            }
            val activeIdx = if (activeClinic != null) clinics.indexOf(activeClinic) else -1

            val htmlContent = """
                <!DOCTYPE html>
                <html>
                  <head>
                    <style>
                      html, body { height: 100%; margin: 0; padding: 0; font-family: sans-serif; }
                      #map { height: 100%; width: 100%; }
                    </style>
                  </head>
                  <body>
                    <div id="map"></div>
                    <script>
                      function initMap() {
                        var map = new google.maps.Map(document.getElementById('map'), {
                          zoom: 2,
                          center: {lat: 20, lng: 0}
                        });
                        var geocoder = new google.maps.Geocoder();
                        var bounds = new google.maps.LatLngBounds();
                        var infowindow = new google.maps.InfoWindow();

                        var locations = [
                            $locationsJson
                        ];

                        var activeIndex = $activeIdx;
                        var processed = 0;
                        var markers = [];

                        if(locations.length === 0) return;

                        locations.forEach(function(loc, i) {
                          geocoder.geocode({'address': loc.address}, function(results, status) {
                            processed++;
                            if (status === 'OK') {
                              var marker = new google.maps.Marker({
                                map: map,
                                position: results[0].geometry.location,
                                title: loc.name
                              });
                              markers.push(marker);
                              bounds.extend(results[0].geometry.location);

                              marker.addListener('click', function() {
                                infowindow.setContent('<div style="font-family:sans-serif; padding:4px;"><b>' + loc.name + '</b><br/>' + loc.address + '</div>');
                                infowindow.open(map, marker);
                              });

                              if (i === activeIndex) {
                                infowindow.setContent('<div style="font-family:sans-serif; padding:4px;"><b>' + loc.name + '</b><br/>' + loc.address + '</div>');
                                infowindow.open(map, marker);
                                map.setZoom(14);
                                map.panTo(marker.position);
                              }
                            }

                            if (processed === locations.length && activeIndex === -1) {
                              if (markers.length > 1) {
                                map.fitBounds(bounds);
                              } else if (markers.length === 1) {
                                map.setCenter(markers[0].position);
                                map.setZoom(13);
                              }
                            }
                          });
                        });
                      }
                    </script>
                    <script async defer src="https://maps.googleapis.com/maps/api/js?key=$googleMapsApiKey&callback=initMap&loading=async"></script>
                  </body>
                </html>
            """.trimIndent()

            Iframe(
                attrs = Modifier.fillMaxSize().border(0.px).toAttrs {
                    // FIXED: We drop 'srcdoc' entirely. By writing directly to the iframe document,
                    // the iframe perfectly inherits your "localhost" or "iregained.com" URL,
                    // passing the Google Maps security check!
                    ref { el ->
                        val iframe = el as org.w3c.dom.HTMLIFrameElement
                        val doc = iframe.contentDocument
                        if (doc != null) {
                            val dynamicDoc = doc.asDynamic()
                            dynamicDoc.open()
                            dynamicDoc.write(htmlContent)
                            dynamicDoc.close()
                        }
                        onDispose {}
                    }
                })
        }
    }
}